import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatError, descargarRecurso } from "@/lib/api";
import { PageHeader, ScopeNotice, useScopeGuard } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Printer, Eye, Save, Link2, FileText, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { modo: "vinculada", lote_id: "", lote_codigo: "", formato: "a4_8", copias: 1, campos_extra: [], titulo: "", lineas: [], fecha: "" };

export default function Etiquetas() {
  const { user, scopeTenant } = useAuth();
  const canLibre = ["superadmin", "admin", "calidad"].includes(user.rol);
  const needsScope = useScopeGuard(user, scopeTenant);

  const [form, setForm] = useState(EMPTY);
  const [buscar, setBuscar] = useState("");
  const [plantillas, setPlantillas] = useState([]);
  const [preview, setPreview] = useState(null);
  const [aviso, setAviso] = useState(null); // { lote_id, codigo } si texto libre coincide con lote

  const loadPlantillas = useCallback(async () => {
    if (needsScope) return;
    const { data } = await api.get("/etiquetas/plantillas");
    setPlantillas(data);
  }, [needsScope]);
  useEffect(() => { loadPlantillas(); }, [loadPlantillas]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const vincularLote = async () => {
    if (!buscar.trim()) return;
    try {
      const { data } = await api.get("/trazabilidad/resolver", { params: { codigo: buscar.trim() } });
      set({ lote_id: data.lote_id, lote_codigo: data.codigo_resuelto }); toast.success(`Lote ${data.codigo_resuelto} vinculado`);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  // Aviso: título libre coincide con un lote existente
  useEffect(() => {
    if (form.modo !== "libre" || !form.titulo?.trim()) { setAviso(null); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get("/etiquetas/verificar", { params: { texto: form.titulo.trim() } });
        setAviso(data.existe ? data : null); } catch { setAviso(null); }
    }, 500);
    return () => clearTimeout(t);
  }, [form.titulo, form.modo]);

  const addCampo = (key) => set({ [key]: [...form[key], { etiqueta: "", valor: "" }] });
  const setCampo = (key, i, patch) => set({ [key]: form[key].map((c, idx) => (idx === i ? { ...c, ...patch } : c)) });
  const rmCampo = (key, i) => set({ [key]: form[key].filter((_, idx) => idx !== i) });

  const payload = () => ({
    modo: form.modo, lote_id: form.modo === "vinculada" ? form.lote_id : null,
    formato: form.formato, copias: Number(form.copias),
    campos_extra: form.campos_extra.filter((c) => c.etiqueta || c.valor),
    titulo: form.titulo, lineas: form.lineas.filter((c) => c.etiqueta || c.valor), fecha: form.fecha || null,
  });

  const verPreview = async () => {
    if (form.modo === "vinculada" && !form.lote_id) return toast.error("Vincule un lote primero");
    if (form.modo === "libre" && !form.titulo.trim() && form.lineas.length === 0) return toast.error("Añada título o líneas");
    try {
      const { data } = await api.post("/etiquetas/preview", payload());
      const bytes = Uint8Array.from(atob(data.pdf_base64), (c) => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
      setPreview((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const generar = async () => {
    if (form.modo === "vinculada" && !form.lote_id) return toast.error("Vincule un lote primero");
    try { const { data } = await api.post("/etiquetas/generar", payload()); toast.success("Etiquetas generadas"); descargarRecurso("etiqueta", data.id); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const guardarPlantilla = async () => {
    const nombre = window.prompt("Nombre de la plantilla:");
    if (!nombre?.trim()) return;
    try {
      await api.post("/etiquetas/plantillas", { nombre, modo: form.modo, titulo: form.titulo, lineas: form.lineas, campos_extra: form.campos_extra, formato: form.formato });
      toast.success("Plantilla guardada"); loadPlantillas();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const cargarPlantilla = (p) => { set({ modo: p.modo, titulo: p.titulo || "", lineas: p.lineas || [], campos_extra: p.campos_extra || [], formato: p.formato || "a4_8" }); toast.success(`Plantilla "${p.nombre}" cargada`); };
  const borrarPlantilla = async (id) => { try { await api.delete(`/etiquetas/plantillas/${id}`); toast.success("Plantilla eliminada"); loadPlantillas(); } catch (e) { toast.error(formatError(e.response?.data?.detail)); } };

  if (needsScope) return <ScopeNotice />;

  return (
    <div data-testid="etiquetas-page">
      <PageHeader overline="Nivel 3 · Traza" title="Diseñador de etiquetas" help="etiquetas" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="space-y-5">
          {/* Modo */}
          <Card className="p-4 border-[#E2E8F0]">
            <div className="flex gap-2">
              <Button variant={form.modo === "vinculada" ? "default" : "outline"} className={form.modo === "vinculada" ? "bg-[#0E2A47]" : ""} onClick={() => set({ modo: "vinculada" })} data-testid="modo-vinculada"><Link2 size={15} className="mr-1" />Vinculada a lote</Button>
              {canLibre && <Button variant={form.modo === "libre" ? "default" : "outline"} className={form.modo === "libre" ? "bg-[#0E2A47]" : ""} onClick={() => set({ modo: "libre" })} data-testid="modo-libre"><FileText size={15} className="mr-1" />Libre</Button>}
            </div>
          </Card>

          {/* Vinculada */}
          {form.modo === "vinculada" && (
            <Card className="p-4 border-[#E2E8F0] space-y-3" data-testid="panel-vinculada">
              <Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Lote</Label>
              <div className="flex gap-2">
                <Input placeholder="Código de lote…" value={buscar} onChange={(e) => setBuscar(e.target.value)} onKeyDown={(e) => e.key === "Enter" && vincularLote()} data-testid="etq-buscar-lote" />
                <Button onClick={vincularLote} data-testid="etq-vincular-btn" className="bg-[#0E2A47]">Vincular</Button>
              </div>
              {form.lote_codigo && <Badge className="bg-[#10B981] hover:bg-[#10B981] text-white">Vinculado: {form.lote_codigo} (QR de trazabilidad real)</Badge>}
              <CamposExtra titulo="Campos extra (texto libre)" campos={form.campos_extra} onAdd={() => addCampo("campos_extra")} onSet={(i, p) => setCampo("campos_extra", i, p)} onRm={(i) => rmCampo("campos_extra", i)} testid="extra" />
            </Card>
          )}

          {/* Libre */}
          {form.modo === "libre" && (
            <Card className="p-4 border-[#E2E8F0] space-y-3" data-testid="panel-libre">
              <div><Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Título</Label>
                <Input value={form.titulo} onChange={(e) => set({ titulo: e.target.value })} data-testid="libre-titulo" className="mt-1.5" /></div>
              {aviso && (
                <div className="text-xs text-[#B45309] bg-[#FFFBEB] border border-[#F59E0B]/40 rounded p-2 flex items-center justify-between gap-2" data-testid="libre-aviso">
                  <span className="flex items-center gap-1"><AlertTriangle size={13} />"{aviso.codigo}" coincide con un lote existente.</span>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { set({ modo: "vinculada", lote_id: aviso.lote_id, lote_codigo: aviso.codigo }); setBuscar(aviso.codigo); }} data-testid="libre-vincular">Vincular al lote</Button>
                </div>
              )}
              <CamposExtra titulo="Líneas (máx. 6)" campos={form.lineas} onAdd={() => form.lineas.length < 6 && addCampo("lineas")} onSet={(i, p) => setCampo("lineas", i, p)} onRm={(i) => rmCampo("lineas", i)} testid="linea" />
              <div><Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Fecha (opcional)</Label>
                <Input type="date" value={form.fecha} onChange={(e) => set({ fecha: e.target.value })} data-testid="libre-fecha" className="mt-1.5" /></div>
              <div className="text-xs text-[#0E2A47]/50">Las etiquetas libres no llevan QR y se marcan con "ETIQUETA MANUAL".</div>
            </Card>
          )}

          {/* Formato + copias + acciones */}
          <Card className="p-4 border-[#E2E8F0] space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div><Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Formato</Label>
                <Select value={form.formato} onValueChange={(v) => set({ formato: v })}>
                  <SelectTrigger data-testid="etq-formato-des" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="a4_8">8 por A4 (105×74mm)</SelectItem><SelectItem value="individual">Individual (100×60mm)</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">Copias</Label>
                <Input type="number" min="1" max="200" value={form.copias} onChange={(e) => set({ copias: e.target.value })} data-testid="etq-copias-des" className="mt-1.5" /></div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={verPreview} data-testid="etq-preview-btn"><Eye size={15} className="mr-1" />Vista previa</Button>
              <Button onClick={generar} data-testid="etq-generar-des" className="bg-[#FF5625] hover:bg-[#E0451A]"><Printer size={15} className="mr-1" />Generar PDF</Button>
              {canLibre && <Button variant="outline" onClick={guardarPlantilla} data-testid="etq-guardar-plantilla"><Save size={15} className="mr-1" />Guardar plantilla</Button>}
            </div>
          </Card>

          {/* Plantillas */}
          {canLibre && (
            <Card className="p-4 border-[#E2E8F0]" data-testid="plantillas-panel">
              <div className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60 mb-2">Plantillas ({plantillas.length})</div>
              {plantillas.length === 0 && <div className="text-xs text-[#0E2A47]/40">Sin plantillas guardadas.</div>}
              <div className="space-y-1">
                {plantillas.map((p) => (
                  <div key={p.id} className="flex items-center justify-between text-sm border-b border-[#E2E8F0] py-1.5" data-testid={`plantilla-${p.id}`}>
                    <span className="text-[#0E2A47]">{p.nombre} <Badge variant="outline" className="text-[10px] ml-1">{p.modo}</Badge></span>
                    <span className="flex gap-1">
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => cargarPlantilla(p)} data-testid={`plantilla-cargar-${p.id}`}>Usar</Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-[#EF4444]" onClick={() => borrarPlantilla(p.id)} data-testid={`plantilla-borrar-${p.id}`}><Trash2 size={13} /></Button>
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>

        {/* Preview */}
        <div>
          <Card className="p-4 border-[#E2E8F0] sticky top-4" data-testid="preview-panel">
            <div className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60 mb-2">Vista previa</div>
            {preview ? <iframe title="preview" src={preview} data-testid="preview-iframe" className="w-full h-[420px] border border-[#E2E8F0] rounded" />
              : <div className="h-[420px] flex items-center justify-center text-[#0E2A47]/30 text-sm border border-dashed border-[#E2E8F0] rounded">Pulse "Vista previa" para ver la etiqueta</div>}
          </Card>
        </div>
      </div>
    </div>
  );
}

function CamposExtra({ titulo, campos, onAdd, onSet, onRm, testid }) {
  return (
    <div>
      <Label className="text-xs font-bold uppercase tracking-wider text-[#0E2A47]/60">{titulo}</Label>
      <div className="space-y-2 mt-1.5">
        {campos.map((c, i) => (
          <div key={i} className="flex flex-wrap sm:flex-nowrap items-center gap-2" data-testid={`${testid}-row-${i}`}>
            <Input placeholder="Etiqueta" value={c.etiqueta} onChange={(e) => onSet(i, { etiqueta: e.target.value })} data-testid={`${testid}-etiqueta-${i}`} className="h-9 flex-1 min-w-[120px]" />
            <Input placeholder="Valor" value={c.valor} onChange={(e) => onSet(i, { valor: e.target.value })} data-testid={`${testid}-valor-${i}`} className="h-9 flex-1 min-w-[120px]" />
            <Button size="icon" variant="ghost" className="h-9 w-9 text-[#EF4444]" onClick={() => onRm(i)} data-testid={`${testid}-rm-${i}`}><Trash2 size={14} /></Button>
          </div>
        ))}
        <Button size="sm" variant="ghost" className="text-[#3B82F6] h-8" onClick={onAdd} data-testid={`add-${testid}`}><Plus size={14} className="mr-1" />Añadir</Button>
      </div>
    </div>
  );
}
